/**
 * Navigation Component
 *
 * Sidebar navigation for the Added To Cart Popup admin interface.
 *
 * @since 1.0.0
 */

import {
  CogIcon,
  EyeIcon,
  PaintBrushIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { colors } from '@redfoxwp/core-components/colors';
import { Card } from '@redfoxwp/core-components/components/card';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { FlexItem } from '@redfoxwp/core-components/components/flex-item';
import { Logo } from '@redfoxwp/core-components/components/logo';
import { __ } from '@wordpress/i18n';
import { useHashLocation } from 'wouter/use-hash-location';

import {
  NavigationItem,
  NavigationItemIcon,
  NavigationItemLabel,
  NavigationMenu,
  PluginName,
  PluginVersion,
} from './styled';
import type { NavigationItemConfig, NavigationItemProps } from './types';

/**
 * Navigation Item component
 *
 * @since 1.0.0
 */
const NavigationItemComponent = ({
  path,
  label,
  description,
  icon,
}: NavigationItemProps): JSX.Element => {
  const [location] = useHashLocation();
  const isActive = location === path;

  return (
    <NavigationItem to={path} title={description} $isActive={isActive}>
      <NavigationItemIcon>{icon}</NavigationItemIcon>
      <NavigationItemLabel>{label}</NavigationItemLabel>
    </NavigationItem>
  );
};

/**
 * Navigation component
 *
 * @since 1.0.0
 *
 * @param props Component props.
 *
 * @return JSX element.
 */
export const Navigation = (): JSX.Element => {
  const navigationItems: NavigationItemConfig[] = [
    {
      path: '/',
      label: __('General', 'redfox-added-to-cart-popup'),
      description: __('Basic settings and configuration', 'redfox-added-to-cart-popup'),
      icon: <CogIcon width={24} height={24} />,
    },
    {
      path: '/display',
      label: __('Display', 'redfox-added-to-cart-popup'),
      description: __('Animation and positioning settings', 'redfox-added-to-cart-popup'),
      icon: <EyeIcon width={24} height={24} />,
    },
    {
      path: '/styling',
      label: __('Styling', 'redfox-added-to-cart-popup'),
      description: __('Colors, fonts, and appearance', 'redfox-added-to-cart-popup'),
      icon: <PaintBrushIcon width={24} height={24} />,
    },

    {
      path: '/advanced',
      label: __('Advanced', 'redfox-added-to-cart-popup'),
      description: __('Custom code and debug options', 'redfox-added-to-cart-popup'),
      icon: <WrenchScrewdriverIcon width={24} height={24} />,
    },
  ];

  return (
    <Card
      styleProps={{ background: colors.white, radius: '10px', stickyTop: '40px' }}
      sticky
      overflowHidden
    >
      <Flex direction='column' gap='0' fullWidth>
        <FlexItem>
          <Card styleProps={{ padding: '30px' }}>
            <Logo />
          </Card>
        </FlexItem>
        <FlexItem>
          <Card styleProps={{ padding: '20px 30px', borderTop: `1px solid ${colors.gray[100]}` }}>
            <PluginName>
              {window.redfoxAddedToCartPopupSettings?.pluginName ??
                __('RedFox Added To Cart Popup', 'redfox-added-to-cart-popup')}
            </PluginName>
            <PluginVersion>
              v{window.redfoxAddedToCartPopupSettings?.pluginVersion ?? '1.0.0'}
            </PluginVersion>
          </Card>
        </FlexItem>
        <FlexItem>
          <NavigationMenu>
            {navigationItems.map((item) => (
              <NavigationItemComponent
                key={item.path}
                path={item.path}
                label={item.label}
                description={item.description}
                icon={item.icon}
              />
            ))}
          </NavigationMenu>
        </FlexItem>
      </Flex>
    </Card>
  );
};
